import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toNumber } from '../../versements-libres/services/versement-allocation';
import {
  amountsFromShares,
  isRppOperation,
  PONCTUEL_KEYS,
  PROGRAMMED_KEYS,
  RachatKeys,
  seededRachatShares,
} from '../services/rachat-allocation';

export interface RachatDisinvestmentMode {
  programmed: boolean;
  amountDriven: boolean;
  keys: RachatKeys;
}

export function resolveRachatDisinvestmentMode(
  entity: any,
  option: any,
): RachatDisinvestmentMode {
  const programmed = !!option?.programmed;
  return {
    programmed,
    amountDriven: programmed || isRppOperation(entity?.operation),
    keys: programmed ? PROGRAMMED_KEYS : PONCTUEL_KEYS,
  };
}

function clamp(n: number, min: number, max: number): number {
  const v = Number(n);
  if (isNaN(v)) return min;
  return Math.max(min, Math.min(max, v));
}
function roundTo(n: number, digits: number): number {
  const v = Number(n);
  if (isNaN(v)) return 0;
  return Number(v.toFixed(digits));
}
function maxFractionDigits(maxDigits: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const s = String(raw).replace(',', '.');
    if (!/^\d*\.?\d*$/.test(s)) return { number: true };
    const [, decimals] = s.split('.');
    if (decimals && decimals.length > maxDigits) {
      return { maxFractionDigits: { max: maxDigits } };
    }
    return null;
  };
}
function maxBySibling(siblingKey: string, digits = 4): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent as UntypedFormGroup | null;
    if (!parent) return null;
    const maxRaw = Number(parent.get(siblingKey)?.value ?? 0);
    const max = roundTo(maxRaw, digits);
    const val = Number(control.value);
    if (isNaN(val)) return null;
    if (val < 0) return { min: { min: 0 } };
    if (val > max) return { max: { max } };
    return null;
  };
}

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  programmed = false;
  amountDriven = false;

  get keys(): RachatKeys {
    return this.programmed ? PROGRAMMED_KEYS : PONCTUEL_KEYS;
  }

  generateForm(
    entity: PledgeProductRead & { parameters: any },
    option,
    mode: RachatDisinvestmentMode = resolveRachatDisinvestmentMode(
      entity,
      option,
    ),
  ) {
    const parameters: Parameters = { ...option };
    const options: AbstractControlOptions = {};
    const { keys, amountDriven, programmed } = mode;

    const supportsDivestment: UntypedFormGroup[] = [];
    const storedRepartition = entity?.parameters?.[keys.supports] || [];
    const currentRepartition =
      programmed && !storedRepartition.length
        ? this.seedFromCurrentAllocation(entity)
        : storedRepartition;

    const seededShares = amountDriven
      ? seededRachatShares(
          _.map(currentRepartition, support =>
            roundTo(toNumber(support?.disinvest) * 100, 4),
          ),
          _.map(currentRepartition, support =>
            toNumber(support?.amountDisinvest),
          ),
          toNumber(entity?.parameters?.[keys.amount]),
        )
      : [];

    if (currentRepartition) {
      _.forEach(currentRepartition, (support, index) =>
        supportsDivestment.push(
          this.formBuilder.group(
            this.createModel(support, amountDriven, seededShares[index]),
          ),
        ),
      );
    }

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        [keys.amount]: [roundTo(entity?.parameters?.[keys.amount] || 0, 4)],
        [keys.supports]: this.formBuilder.array(
          supportsDivestment,
          Validators.required,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generate(entity: any, option) {
    const mode = resolveRachatDisinvestmentMode(entity, option);
    this.programmed = mode.programmed;
    this.amountDriven = mode.amountDriven;
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: (product, opts) => this.generateForm(product, opts, mode),
      propertyName: 'products',
    });
  }

  private seedFromCurrentAllocation(
    entity: PledgeProductRead & { parameters: any },
  ) {
    const lines =
      entity?.parameters?.currentSupportRepartition?.supportRepartition || [];

    return lines
      .filter(line => !!line?.isin && toNumber(line?.amount) > 0)
      .map(line => ({
        id: line.supportId,
        name: line.supportName,
        type: line.supportType,
        isin: line.isin,
        details: null,
        disinvest: 0,
        amountDisinvest: 0,
        currentAllocationAmount: line.amount,
      }));
  }

  public createModel(item, amountDriven = false, seededShare = 0) {
    if (amountDriven) return this.createRachatModel(item, seededShare);

    return {
      id: [item ? item.id : null],
      name: [item ? item.name : null],
      type: [item ? item.type : null],
      isin: [item ? item.isin : null],
      details: [item ? item.details : null],

      disinvest: [
        item && !_.isNil(item.disinvest) ? roundTo(item.disinvest * 100, 4) : 0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          maxFractionDigits(4),
        ],
      ],

      amountDisinvest: [
        item && item.amountDisinvest ? roundTo(item.amountDisinvest, 4) : 0,
        [Validators.required, maxBySibling('currentAllocationAmount', 4)],
      ],

      currentAllocationAmount: [
        item && item.currentAllocationAmount
          ? roundTo(item.currentAllocationAmount, 4)
          : 0,
      ],
    };
  }

  private createRachatModel(item, seededShare = 0) {
    const currentAllocationAmount =
      item && item.currentAllocationAmount
        ? roundTo(item.currentAllocationAmount, 2)
        : 0;
    const amountDisinvest =
      item && item.amountDisinvest ? roundTo(item.amountDisinvest, 2) : 0;

    return {
      id: [item ? item.id : null],
      name: [item ? item.name : null],
      type: [item ? item.type : null],
      isin: [item ? item.isin : null],
      details: [item ? item.details : null],

      disinvest: [
        roundTo(seededShare || 0, 4),
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          maxFractionDigits(4),
        ],
      ],

      amountDisinvest: [
        amountDisinvest,
        [Validators.required, maxBySibling('currentAllocationAmount', 2)],
      ],

      currentAllocationAmount: [currentAllocationAmount],
    };
  }

  private _syncedDis = new WeakSet<UntypedFormGroup>();
  private _syncedRachat = new WeakSet<UntypedFormGroup>();

  attachDivestmentSync(destroy$: Subject<void>) {
    const product = this.products?.get(`${0}`) as UntypedFormGroup;
    if (product) this.attachDivestmentSyncProduct(product, destroy$);
  }

  private attachDivestmentSyncProduct(
    product: UntypedFormGroup,
    destroy$: Subject<void>,
  ) {
    const params = product.get('parameters') as UntypedFormGroup;
    if (!params) return;

    const supports = params.get(this.keys.supports) as UntypedFormArray;
    if (!supports || !supports.length) return;

    const attachOne = (ctrl: UntypedFormGroup) => {
      if (this._syncedDis.has(ctrl)) return;
      this._syncedDis.add(ctrl);

      const percentCtrl = ctrl.get('disinvest');
      const amountCtrl = ctrl.get('amountDisinvest');
      const currentCtrl = ctrl.get('currentAllocationAmount');
      if (!percentCtrl || !amountCtrl || !currentCtrl) return;

      let syncing = false;

      percentCtrl.valueChanges
        .pipe(takeUntil(destroy$))
        .subscribe((raw: any) => {
          if (syncing) return;
          syncing = true;

          const current = Number(currentCtrl.value) || 0;

          let p = Number(String(raw).replace(',', '.'));
          if (isNaN(p)) p = 0;
          p = clamp(p, 0, 100);
          const p4 = roundTo(p, 4);
          if (p4 !== Number(raw)) {
            percentCtrl.setValue(p4, { emitEvent: false });
          }

          const max4 = roundTo(current, 4);
          const amount = Math.min(roundTo(current * (p4 / 100), 4), max4);
          amountCtrl.setValue(amount, { emitEvent: false });
          amountCtrl.updateValueAndValidity({ emitEvent: false });

          syncing = false;
        });

      amountCtrl.valueChanges
        .pipe(takeUntil(destroy$))
        .subscribe((raw: any) => {
          if (syncing) return;
          syncing = true;

          const current = Number(currentCtrl.value) || 0;

          let amt = Number(String(raw).replace(',', '.'));
          if (isNaN(amt)) amt = 0;
          amt = clamp(amt, 0, roundTo(current, 4));
          const amt4 = roundTo(amt, 4);
          if (amt4 !== Number(raw)) {
            amountCtrl.setValue(amt4, { emitEvent: false });
          }

          const p = current > 0 ? (amt4 / current) * 100 : 0;
          const p4 = roundTo(clamp(p, 0, 100), 4);
          percentCtrl.setValue(p4, { emitEvent: false });
          percentCtrl.updateValueAndValidity({ emitEvent: false });

          syncing = false;
        });

      currentCtrl.valueChanges.pipe(takeUntil(destroy$)).subscribe(() => {
        const current = Number(currentCtrl.value) || 0;
        const p = Number(percentCtrl.value) || 0;
        const amount = roundTo(current * (clamp(p, 0, 100) / 100), 4);
        amountCtrl.setValue(amount, { emitEvent: false });
        amountCtrl.updateValueAndValidity({ emitEvent: false });
      });

      const initP = clamp(Number(percentCtrl.value) || 0, 0, 100);
      percentCtrl.setValue(roundTo(initP, 4), { emitEvent: true });
    };

    supports.controls.forEach((c: UntypedFormGroup) => attachOne(c));
    supports.valueChanges.pipe(takeUntil(destroy$)).subscribe(() => {
      supports.controls.forEach((c: UntypedFormGroup) => attachOne(c));
    });
  }

  attachRachatSync(destroy$: Subject<void>) {
    const supports = this.supportsDivestment;
    if (!supports || !supports.length) return;

    const rows = () => supports.controls as UntypedFormGroup[];
    const targetAmount = () => toNumber(this.rachatAmountControl?.value);

    let redistributing = false;

    const recomputeAmounts = () => {
      redistributing = true;
      const shares = rows().map(row => toNumber(row.get('disinvest')?.value));
      const amounts = amountsFromShares(shares, targetAmount());
      rows().forEach((row, index) => {
        const amountCtrl = row.get('amountDisinvest');
        amountCtrl.setValue(amounts[index], { emitEvent: false });
        amountCtrl.updateValueAndValidity({ emitEvent: false });
      });
      redistributing = false;
    };

    const attachOne = (row: UntypedFormGroup) => {
      if (this._syncedRachat.has(row)) return;
      this._syncedRachat.add(row);

      const shareCtrl = row.get('disinvest');
      const amountCtrl = row.get('amountDisinvest');
      const currentCtrl = row.get('currentAllocationAmount');
      if (!shareCtrl || !amountCtrl || !currentCtrl) return;

      shareCtrl.valueChanges.pipe(takeUntil(destroy$)).subscribe((raw: any) => {
        if (redistributing) return;
        const share = roundTo(clamp(toNumber(raw), 0, 100), 4);
        if (share !== toNumber(raw)) {
          shareCtrl.setValue(share, { emitEvent: false });
        }
        recomputeAmounts();
      });

      amountCtrl.valueChanges
        .pipe(takeUntil(destroy$))
        .subscribe((raw: any) => {
          if (redistributing) return;
          redistributing = true;

          const target = targetAmount();
          const current = toNumber(currentCtrl.value);
          const cap = target > 0 ? Math.min(current, target) : current;
          const amount = roundTo(clamp(toNumber(raw), 0, cap), 2);
          if (amount !== toNumber(raw)) {
            amountCtrl.setValue(amount, { emitEvent: false });
          }

          const share = target > 0 ? roundTo((amount / target) * 100, 4) : 0;
          shareCtrl.setValue(share, { emitEvent: false });
          shareCtrl.updateValueAndValidity({ emitEvent: false });

          redistributing = false;
        });
    };

    rows().forEach(attachOne);
    supports.valueChanges.pipe(takeUntil(destroy$)).subscribe(() => {
      rows().forEach(attachOne);
    });
    this.rachatAmountControl?.valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe(() => recomputeAmounts());

    recomputeAmounts();
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportsDivestment() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get(this.keys.supports) as UntypedFormArray;
  }
  get rachatAmountControl() {
    return this.products.get(`${0}`).get('parameters').get(this.keys.amount);
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    const amountDriven = this.amountDriven;
    products.forEach(product => {
      let supportsDivestment = _.cloneDeep(
        product.parameters[this.keys.supports],
      );

      if (amountDriven) {
        supportsDivestment.forEach(support => {
          const current = toNumber(support.currentAllocationAmount);
          support.amountDisinvest = roundTo(
            clamp(toNumber(support.amountDisinvest), 0, current),
            2,
          );
          support.disinvest = roundTo(toNumber(support.disinvest) / 100, 6);
        });
      } else {
        supportsDivestment.forEach(support => {
          const current = Number(support.currentAllocationAmount) || 0;

          if (!_.isNil(support.amountDisinvest) && current > 0) {
            const p = (Number(support.amountDisinvest) / current) * 100;
            support.disinvest = roundTo(clamp(p, 0, 100), 4);
          }

          const ratio = !_.isNil(support.disinvest)
            ? Number(support.disinvest) / 100
            : 0;
          support.disinvest = ratio;

          support.amountDisinvest = roundTo(current * ratio, 4);
        });
      }

      product.parameters[this.keys.supports] = supportsDivestment;

      const total = supportsDivestment.reduce(
        (acc, s) => acc + (Number(s.amountDisinvest) || 0),
        0,
      );
      product.parameters[this.keys.amount] = roundTo(
        total,
        amountDriven ? 2 : 4,
      );
    });

    return products;
  }
}
